<script lang="ts">
  // Picks the optional activity types to sync from a service. The sport's own types always sync.
  // `save` answers whether the types were saved; the dialog stays open when they weren't.
  import { Button } from '$lib/components/ui/button'
  import { Checkbox } from '$lib/components/ui/checkbox'
  import * as Dialog from '$lib/components/ui/dialog'
  import { focusMainButton } from '@/Util/utils'

  export let open = false
  export let id: string
  export let serviceName: string
  export let defaultTypes: string[] = []
  export let typeOptions: { value: string, checked: boolean }[]
  export let label: (type: string) => string = (type) => type
  /** With no locked types, at least one has to be picked. */
  export let requireOne = !defaultTypes.length
  export let save: (types: string[]) => Promise<boolean>

  let picked: Record<string, boolean> = {}
  let saving = false

  // Start from the saved choice each time the dialog opens.
  $: if (open) picked = Object.fromEntries(typeOptions.map((t) => [t.value, t.checked]))
  $: if (open) focusMainButton()
  $: sorted = [...typeOptions].sort((a, b) => label(a.value).localeCompare(label(b.value)))
  $: none = !typeOptions.some((t) => picked[t.value])
  $: many = typeOptions.length > 8

  const pickAll = (on: boolean) => (picked = Object.fromEntries(typeOptions.map((t) => [t.value, on])))

  async function submit() {
    saving = true
    const ok = await save(typeOptions.filter((t) => picked[t.value]).map((t) => t.value))
    saving = false
    if (!ok) return
    typeOptions = typeOptions.map((t) => ({ ...t, checked: !!picked[t.value] }))
    open = false
  }
</script>

<Dialog.Root portal="#shadcn-ui-dialog-target" bind:open>
  <Dialog.Content class="max-h-[90vh] max-w-xl grid-rows-[auto_minmax(0,1fr)_auto]">
    <Dialog.Header>
      <Dialog.Title>Activity types</Dialog.Title>
      <Dialog.Description>
        {serviceName} activities of these types are imported into Xert.
        {#if defaultTypes.length}{defaultTypes.map(label).join(', ')} always {defaultTypes.length === 1 ? 'syncs' : 'sync'} for your sport.{/if}
      </Dialog.Description>
    </Dialog.Header>
    <div class="-mx-2 overflow-y-auto px-2">
      <div class="grid grid-cols-1 gap-1 sm:grid-cols-2">
        {#each sorted as t (t.value)}
          <div class="flex items-center gap-3 rounded-md px-2 hover:bg-accent">
            <Checkbox id="{id}-type-{t.value}" checked={picked[t.value]} onCheckedChange={(v) => (picked[t.value] = v === true)} />
            <label for="{id}-type-{t.value}" class="m-0 flex-1 cursor-pointer py-2 text-sm font-normal text-foreground">{label(t.value)}</label>
          </div>
        {/each}
      </div>
    </div>
    <Dialog.Footer class="gap-2 sm:items-center">
      {#if many}
        <span class="mr-auto flex gap-1">
          <Button variant="ghost" size="sm" on:click={() => pickAll(true)}>Select all</Button>
          <Button variant="ghost" size="sm" on:click={() => pickAll(false)}>Clear</Button>
        </span>
      {/if}
      {#if requireOne && none}<span class="text-xs text-muted-foreground">Pick at least one type.</span>{/if}
      <Button variant="outline" on:click={() => (open = false)}>Cancel</Button>
      <Button on:click={submit} disabled={saving || (requireOne && none)} data-main-button>
        {#if saving}<i class="fa-solid fa-spinner-third fa-spin mr-2" aria-hidden="true" />{/if}Save
      </Button>
    </Dialog.Footer>
  </Dialog.Content>
</Dialog.Root>
